import { createLogger } from '../../Utils/Logger';
import { TypeSafeEventSystem } from './EventSystem';
import { ECSEventType, EventPriority, EventTypeValidator } from '../CoreEvents';
/**
 * 增强的事件总线实现
 * 基于TypeSafeEventSystem，提供类型安全的事件发布订阅机制
 */
export class EventBus {
    constructor(debugMode = false) {
        this.eventIdCounter = 0;
        this.isDebugMode = false;
        this.eventSystem = new TypeSafeEventSystem();
        this.isDebugMode = debugMode;
    }
    /**
     * 发射事件
     * @param eventType 事件类型
     * @param data 事件数据
     * @param enhance 是否增强事件数据（添加timestamp、eventId等），默认false提升性能
     */
    emit(eventType, data, enhance = false) {
        this.validateEventType(eventType);
        const finalData = enhance ? this.enhanceEventData(eventType, data) : data;
        if (this.isDebugMode) {
            EventBus._logger.info(`发射事件: ${eventType}`, finalData);
        }
        this.eventSystem.emitSync(eventType, finalData);
    }
    /**
     * 异步发射事件
     * @param eventType 事件类型
     * @param data 事件数据
     * @param enhance 是否增强事件数据（添加timestamp、eventId等），默认false提升性能
     */
    async emitAsync(eventType, data, enhance = false) {
        this.validateEventType(eventType);
        const finalData = enhance ? this.enhanceEventData(eventType, data) : data;
        if (this.isDebugMode) {
            EventBus._logger.info(`发射异步事件: ${eventType}`, finalData);
        }
        await this.eventSystem.emit(eventType, finalData);
    }
    /**
     * 监听事件
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @param config 监听器配置
     * @returns 监听器ID
     */
    on(eventType, handler, config = {}) {
        this.validateEventType(eventType);
        const eventConfig = {
            once: config.once || false,
            priority: config.priority || EventPriority.NORMAL,
            async: config.async || false
        };
        if (config.thisArg) {
            eventConfig.thisArg = config.thisArg;
        }
        if (this.isDebugMode) {
            EventBus._logger.info(`添加监听器: ${eventType}`, eventConfig);
        }
        return this.eventSystem.on(eventType, handler, eventConfig);
    }
    /**
     * 监听事件（一次性）
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @param config 监听器配置
     * @returns 监听器ID
     */
    once(eventType, handler, config = {}) {
        return this.on(eventType, handler, { ...config, once: true });
    }
    /**
     * 异步监听事件
     * @param eventType 事件类型
     * @param handler 异步事件处理器
     * @param config 监听器配置
     * @returns 监听器ID
     */
    onAsync(eventType, handler, config = {}) {
        return this.on(eventType, handler, { ...config, async: true });
    }
    /**
     * 移除事件监听器
     * @param eventType 事件类型
     * @param listenerId 监听器ID
     */
    off(eventType, listenerId) {
        if (this.isDebugMode) {
            EventBus._logger.info(`移除监听器: ${listenerId} 事件: ${eventType}`);
        }
        return this.eventSystem.off(eventType, listenerId);
    }
    /**
     * 移除指定事件类型的所有监听器
     * @param eventType 事件类型
     */
    offAll(eventType) {
        if (this.isDebugMode) {
            EventBus._logger.info(`移除所有监听器: ${eventType}`);
        }
        this.eventSystem.offAll(eventType);
    }
    /**
     * 检查是否有指定事件的监听器
     * @param eventType 事件类型
     */
    hasListeners(eventType) {
        return this.eventSystem.hasListeners(eventType);
    }
    /**
     * 获取事件统计信息
     * @param eventType 事件类型（可选）
     */
    getStats(eventType) {
        const stats = this.eventSystem.getStats(eventType);
        if (stats instanceof Map) {
            // 转换Map中的每个EventStats为IEventStats
            const result = new Map();
            stats.forEach((stat, key) => {
                result.set(key, this.convertEventStats(stat));
            });
            return result;
        }
        else {
            return this.convertEventStats(stats);
        }
    }
    /**
     * 清空所有监听器
     */
    clear() {
        if (this.isDebugMode) {
            EventBus._logger.info('清空所有监听器');
        }
        this.eventSystem.clear();
    }
    /**
     * 启用或禁用事件系统
     * @param enabled 是否启用
     */
    setEnabled(enabled) {
        this.eventSystem.setEnabled(enabled);
    }
    /**
     * 设置调试模式
     * @param debug 是否启用调试
     */
    setDebugMode(debug) {
        this.isDebugMode = debug;
    }
    /**
     * 设置最大监听器数量
     * @param max 最大数量
     */
    setMaxListeners(max) {
        this.eventSystem.setMaxListeners(max);
    }
    /**
     * 获取监听器数量
     * @param eventType 事件类型
     */
    getListenerCount(eventType) {
        return this.eventSystem.getListenerCount(eventType);
    }
    /**
     * 设置事件批处理配置
     * @param eventType 事件类型
     * @param batchSize 批处理大小
     * @param delay 延迟时间（毫秒）
     */
    setBatchConfig(eventType, batchSize, delay) {
        this.eventSystem.setBatchConfig(eventType, {
            batchSize,
            delay,
            enabled: true
        });
    }
    /**
     * 刷新指定事件的批处理队列
     * @param eventType 事件类型
     */
    flushBatch(eventType) {
        this.eventSystem.flushBatch(eventType);
    }
    /**
     * 重置事件统计
     * @param eventType 事件类型（可选）
     */
    resetStats(eventType) {
        this.eventSystem.resetStats(eventType);
    }
    // 便捷方法：发射预定义的ECS事件
    /**
     * 发射实体创建事件
     * @param entityData 实体事件数据
     */
    emitEntityCreated(entityData) {
        this.emit(ECSEventType.ENTITY_CREATED, entityData);
    }
    /**
     * 发射实体销毁事件
     * @param entityData 实体事件数据
     */
    emitEntityDestroyed(entityData) {
        this.emit(ECSEventType.ENTITY_DESTROYED, entityData);
    }
    /**
     * 发射组件添加事件
     * @param componentData 组件事件数据
     */
    emitComponentAdded(componentData) {
        this.emit(ECSEventType.COMPONENT_ADDED, componentData);
    }
    /**
     * 发射组件移除事件
     * @param componentData 组件事件数据
     */
    emitComponentRemoved(componentData) {
        this.emit(ECSEventType.COMPONENT_REMOVED, componentData);
    }
    /**
     * 发射系统添加事件
     * @param systemData 系统事件数据
     */
    emitSystemAdded(systemData) {
        this.emit(ECSEventType.SYSTEM_ADDED, systemData);
    }
    /**
     * 发射系统移除事件
     * @param systemData 系统事件数据
     */
    emitSystemRemoved(systemData) {
        this.emit(ECSEventType.SYSTEM_REMOVED, systemData);
    }
    /**
     * 发射场景变化事件
     * @param sceneData 场景事件数据
     */
    emitSceneChanged(sceneData) {
        this.emit(ECSEventType.SCENE_ACTIVATED, sceneData);
    }
    /**
     * 发射性能警告事件
     * @param performanceData 性能事件数据
     */
    emitPerformanceWarning(performanceData) {
        this.emit(ECSEventType.PERFORMANCE_WARNING, performanceData);
    }
    // 便捷方法：监听预定义的ECS事件
    /**
     * 监听实体创建事件
     * @param handler 事件处理器
     * @param config 监听器配置
     */
    onEntityCreated(handler, config) {
        return this.on(ECSEventType.ENTITY_CREATED, handler, config);
    }
    /**
     * 监听组件添加事件
     * @param handler 事件处理器
     * @param config 监听器配置
     */
    onComponentAdded(handler, config) {
        return this.on(ECSEventType.COMPONENT_ADDED, handler, config);
    }
    /**
     * 监听系统错误事件
     * @param handler 事件处理器
     * @param config 监听器配置
     */
    onSystemError(handler, config) {
        return this.on(ECSEventType.SYSTEM_ERROR, handler, config);
    }
    /**
     * 监听性能警告事件
     * @param handler 事件处理器
     * @param config 监听器配置
     */
    onPerformanceWarning(handler, config) {
        return this.on(ECSEventType.PERFORMANCE_WARNING, handler, config);
    }
    // 私有方法
    /**
     * 验证事件类型（仅在debug模式下执行，提升性能）
     * @param eventType 事件类型
     */
    validateEventType(eventType) {
        // 只在debug模式下进行验证，提升生产环境性能
        if (this.isDebugMode) {
            if (!EventTypeValidator.isValid(eventType)) {
                EventBus._logger.warn(`未知事件类型: ${eventType}`);
                // 在调试模式下添加自定义事件类型
                EventTypeValidator.addCustomType(eventType);
            }
        }
    }
    /**
     * 增强事件数据
     * @param eventType 事件类型
     * @param data 原始数据
     */
    enhanceEventData(eventType, data) {
        // 处理null或undefined数据
        if (data === null || data === undefined) {
            return {
                timestamp: Date.now(),
                eventId: `${eventType}_${++this.eventIdCounter}`,
                source: 'EventBus'
            };
        }
        const enhanced = data;
        // 如果数据还没有基础事件属性，添加它们
        if (!enhanced.timestamp) {
            enhanced.timestamp = Date.now();
        }
        if (!enhanced.eventId) {
            enhanced.eventId = `${eventType}_${++this.eventIdCounter}`;
        }
        if (!enhanced.source) {
            enhanced.source = 'EventBus';
        }
        return enhanced;
    }
    /**
     * 转换EventStats为IEventStats
     * @param stats EventStats实例
     */
    convertEventStats(stats) {
        return {
            eventType: stats.eventType,
            listenerCount: stats.listenerCount,
            triggerCount: stats.triggerCount,
            totalExecutionTime: stats.totalExecutionTime,
            averageExecutionTime: stats.averageExecutionTime,
            lastTriggerTime: stats.lastTriggerTime
        };
    }
}
EventBus._logger = createLogger('EventBus');
/**
 * 全局事件总线实例
 * 提供全局访问的事件总线
 */
export class GlobalEventBus {
    /**
     * @zh 获取全局事件总线实例
     * @en Get global event bus instance
     *
     * @param debugMode - @zh 是否启用调试模式 @en Whether to enable debug mode
     */
    static getInstance(debugMode = false) {
        if (!this._instance) {
            this._instance = new EventBus(debugMode);
        }
        return this._instance;
    }
    /**
     * @zh 重置全局事件总线实例
     * @en Reset global event bus instance
     *
     * @param debugMode - @zh 是否启用调试模式 @en Whether to enable debug mode
     */
    static reset(debugMode = false) {
        if (this._instance) {
            this._instance.clear();
        }
        this._instance = new EventBus(debugMode);
        return this._instance;
    }
}
//# sourceMappingURL=EventBus.js.map